import { makeStyles, Theme } from '@material-ui/core';
import { ModeAutoCompleteInputField, ModeSelectInputField, SelectInputOption, useModeFormStyle } from '@moderepo/mode-app-components';
import { AttributeValueCompareOperator, Entity, EntityAttributeParam, EntityAttributeType, EntityAttributeValueType }
    from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React from 'react';
import { EntityInputComp, EntityInputCompProps } from '.';


/**
 * Define all the different entity filter components
 */


const useStyle = makeStyles((theme: Theme) => {

    return {
        filterInputField: {
            flex              : 1,
            marginRight       : theme.spacing(2),
            '& .MuiInput-root': {
                height: '100%',
            },
        },
    };

});



/**
 * Interface for the filter settings for which currentValue is just a single value, number | string
 */
export interface FilterSettings <T extends number | string | boolean | object | Array<any>> {
    readonly label: string;
    readonly currentValue: T | undefined;
    readonly options: readonly SelectInputOption<T>[];
}



/**
 * Interface for the filter settings for which currentValue is an ARRAY of values.
 */
export interface FilterArraySettings <T extends number | string | boolean | object | Array<any>> extends Omit<FilterSettings<T>, 'currentValue'> {
    readonly currentValue: readonly T[];
}



/**
 * Interface for the filter settings for Entity filter option
 */
export interface EntityFilterSettings {
    readonly label: string;
    readonly currentValue: Entity | undefined;
    readonly entitiesListingContainerProps: EntityInputCompProps['entitiesListingContainerProps'];
    readonly texts: EntityInputCompProps['texts'];
}



/**
 * Interface for attribute filter settings. This interface inherit FilterArraySettings interface but will override the
 * currentValue's type and options.
 */
export interface AttributeFilterSettings<T extends number | string | boolean | object | Array<any>>
    extends Omit<FilterArraySettings<T>, 'currentValue' | 'options'> {

    readonly currentValue: undefined;
    readonly attributeOptions: readonly SelectInputOption<EntityAttributeParam<EntityAttributeValueType>>[];
    readonly operatorOptions: {
        readonly [EntityAttributeType.NUMBER]: readonly SelectInputOption<AttributeValueCompareOperator>[],
        readonly [EntityAttributeType.BOOLEAN]: readonly SelectInputOption<AttributeValueCompareOperator>[]
        readonly [EntityAttributeType.STRING]: readonly SelectInputOption<AttributeValueCompareOperator>[]
    };
}



/**
 * Generic SELECT input component used for filter options that has 1 single value
 */
export const GenericFilterInput = <T extends number | string>(props: {
    readonly filterSettings: FilterSettings<T>;
    readonly currentValue: T | undefined;
    readonly setCurrentValue: (value: T)=> void
    readonly disabled?: boolean | undefined;
}) => {
    const formClasses = useModeFormStyle();
    const classes = useStyle();
    return (
        <ModeSelectInputField
            className={clsx(formClasses.formInputField, classes.filterInputField)}
            fieldName="entityClasses"
            value={props.currentValue}
            onInputChange={(fieldName, value) => {
                props.setCurrentValue(value as T);
            }}
            noOptionsLabel="none"
            isInEditMode
            editable={!props.disabled}
            label={props.filterSettings.label}
            options={props.filterSettings.options}
        />
    );
};



/**
 * SELECT input component used for entity classes filter which allow 1 or more selection
 */
export const EntityClassesFilterInput = (props: {
    readonly filterSettings: FilterArraySettings<string>;
    readonly currentValue: readonly string[] | undefined;
    readonly setCurrentValue: (value: readonly string[])=> void;
    readonly emptyInputMessage?: string | undefined; // Message to show when there is no input
}) => {
    const formClasses = useModeFormStyle();
    const classes = useStyle();
    return (
        <ModeAutoCompleteInputField
            className={clsx(formClasses.formInputField, classes.filterInputField)}
            fieldName="entityClasses"
            value={props.currentValue}
            onInputChange={(fieldName: string, value: string[]) => {
                props.setCurrentValue(value);
            }}
            noOptionsLabel="none"
            isInEditMode
            editable
            label={props.filterSettings.label}
            options={props.filterSettings.options}
            multiple
            multipleSelectionDisplayLimit={2}
            placeholder={!props.currentValue || props.currentValue.length <= 0 ? props.emptyInputMessage : undefined}
        />
    );
};



/**
 * Entity SELECT input component used for selecting an entity
 */
export const EntityFilterInput = (props: {
    readonly className?: string | undefined;
    readonly filterSettings: EntityFilterSettings;
    readonly currentValue: Entity | undefined;
    readonly setCurrentValue: (value: Entity | undefined)=> void;
    readonly emptyInputMessage?: string | undefined;
}) => {
    const formClasses = useModeFormStyle();
    const classes = useStyle();

    return (
        <EntityInputComp
            className={clsx(props.className, formClasses.formInputField, classes.filterInputField)}
            fieldName="entity"
            isEditing={true}
            editable={true}
            entitiesListingContainerProps={props.filterSettings.entitiesListingContainerProps}
            texts={props.filterSettings.texts}
            label={props.filterSettings.label}
            value={props.currentValue}
            onFormInputChange={(fieldName: string, entity: Entity | undefined) => {
                props.setCurrentValue(entity);
            }}
            placeholder={props.emptyInputMessage}
        />
    );
};
